/**
 * Regras de navegação do menu lateral do admin (AppSidebar).
 *
 * Mora fora do componente porque o react-refresh só faz hot reload de arquivos
 * que exportam exclusivamente componentes — e porque estas regras são testáveis
 * sozinhas, sem montar a sidebar inteira.
 */

export interface SidebarPageProps {
  // Presente em /users/:id — é o usuário exibido na página, não quem está logado.
  user?: { role?: string }
  auth?: { crm_new_retention_count?: number }
  [key: string]: unknown
}

/** O que a sidebar precisa saber sobre quem está logado. */
export interface SidebarAuthUser {
  role?: string
  permissions?: string[]
}

/**
 * Este usuário enxerga este módulo?
 *
 * O servidor já resolve `permissions` (dono e acesso total chegam com a lista
 * completa), então aqui não se reimplementa a regra — só se consulta. Se a lista
 * não veio, ninguém vê nada: falhar fechado é o certo num menu de permissões, e
 * o portão do servidor barra de qualquer jeito.
 */
export function canSeeItem(
  user: SidebarAuthUser | null | undefined,
  moduleKey: string,
): boolean {
  if (!user) return false
  if (user.role === 'owner') return true

  return (user.permissions ?? []).includes(moduleKey)
}

export const OPEN_GROUPS_KEY = 'bewave:sidebar-groups'

export function readOpenGroups(): string[] {
  try {
    const saved: unknown = JSON.parse(
      localStorage.getItem(OPEN_GROUPS_KEY) || '[]',
    )
    return Array.isArray(saved)
      ? saved.filter((id): id is string => typeof id === 'string')
      : []
  } catch {
    // localStorage indisponível (modo privado, cookies bloqueados).
    return []
  }
}

export function persistOpenGroups(ids: Set<string>): void {
  try {
    localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify([...ids]))
  } catch {
    // Idem: abrir e fechar segue funcionando na sessão, só não sobrevive ao reload.
  }
}

/**
 * Um item está ativo quando a rota atual é ele ou está abaixo dele — com as
 * exceções que a hierarquia de URLs não resolve sozinha.
 */
export function isItemActive(
  url: string,
  currentPath: string,
  pageProps: SidebarPageProps,
): boolean {
  // Remove query params da URL para comparação
  const pathWithoutQuery = currentPath.split('?')[0]
  let isActive =
    pathWithoutQuery === url || pathWithoutQuery.startsWith(url + '/')

  // A Ocupação mora sob /lessons, então "Agenda" casaria com ela pelo prefixo.
  // Quem está num relatório de aulas não está na agenda.
  if (url === '/lessons' && pathWithoutQuery.startsWith('/lessons/occupancy')) {
    isActive = false
  }

  // Caso especial: quando está em /users/:id, verifica o role do usuário na página
  if (pathWithoutQuery.match(/^\/users\/\d+/)) {
    const userRole = pageProps?.user?.role

    if (userRole === 'student' && url === '/students') {
      isActive = true
    } else if (
      (userRole === 'teacher' || userRole === 'manager') &&
      url === '/team'
    ) {
      isActive = true
    }
  }

  // Outros casos especiais
  if (
    url === '/student_subscriptions' &&
    pathWithoutQuery.startsWith('/student_subscriptions')
  ) {
    isActive = true
  }

  return isActive
}
